import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "df_token";
const USER_KEY = "df_user";

function loadCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistSession(token, user) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {}
}

function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
}

export const AuthProvider = ({ children }) => {
  // Seed from the last known session so a fresh page load (which is what iOS
  // does every time it resumes a backgrounded PWA) can render the driver's
  // dashboard immediately instead of bouncing to the login screen while the
  // network comes back up.
  const cachedUser = loadCachedUser();
  const [user, setUser] = useState(cachedUser || null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(!cachedUser);

  useEffect(() => {
    let cancelled = false;

    // iOS frequently kills the PWA's page while backgrounded and reloads it from
    // scratch on resume, so this runs far more often than a normal SPA mount —
    // typically right as the device's network radio is still reconnecting. A
    // transient failure here must NOT be treated the same as an invalid session,
    // or the driver gets logged out every time they glance at their phone.
    const verify = async (attempt = 0) => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        // Response is the flat user object with a sibling "token" field (a
        // freshly-renewed one — see the sliding-session note on the backend
        // route) — split it out before storing the rest as the user.
        const { token, ...userData } = data;
        setUser(userData);
        persistSession(token, userData);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Server explicitly rejected the token (expired/invalid/revoked) —
          // this is a genuine logout, not a network hiccup.
          clearSession();
          setUser(false);
          setLoading(false);
          return;
        }
        if (attempt < 3) {
          // No response (offline, DNS/TLS still reconnecting, request dropped
          // mid-handover) — retry with backoff before giving up, without
          // flipping `loading` in between so a cached session stays rendered.
          setTimeout(() => verify(attempt + 1), 1000 * (attempt + 1));
          return;
        }
        // Retries exhausted and the server is still unreachable.
        if (!cachedUser) setUser(false); // nothing to fall back on
        setLoading(false); // otherwise keep the cached session; re-verify next mount
      }
    };

    verify();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persistSession(data.token, data.user);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persistSession(data.token, data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSession();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
